import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Eye, Check, Trash2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const LeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/leads?status=${filter}`);
      setLeads(response.data || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const markAsViewed = async (leadId) => {
    try {
      await api.patch(`/leads/${leadId}`, { viewedBy: 'admin', viewedAt: new Date().toISOString() });
      fetchLeads();
      toast.success('Marked as viewed');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const markAsContacted = async (leadId) => {
    try {
      await api.patch(`/leads/${leadId}`, { status: 'contacted', contactedAt: new Date().toISOString() });
      fetchLeads();
      toast.success('Marked as contacted');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const convertLead = async (leadId) => {
    if (!confirm('Mark as converted and move to customers?')) return;
    try {
      await api.patch(`/leads/${leadId}`, { status: 'converted', convertedAt: new Date().toISOString() });
      fetchLeads();
      toast.success('Lead converted!');
    } catch (error) {
      toast.error('Failed to convert');
    }
  };

  const deleteLead = async (leadId) => {
    if (!confirm('Delete this lead?')) return;
    try {
      await api.delete(`/leads/${leadId}`);
      fetchLeads();
      toast.success('Lead deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const formatDateTime = (dateStr, timeStr) => {
    const date = new Date(dateStr);
    const [hours, mins] = timeStr.split(':');
    const hour24 = parseInt(hours);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'pm' : 'am';
    return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at ${hour12}:${mins}${ampm}`;
  };

  const groupLeadsByTime = (leads) => {
    const now = new Date();
    const groups = {
      'Today': [],
      'Yesterday': [],
      'This Week': [],
      'Older': []
    };

    leads.forEach(lead => {
      const createdDate = new Date(lead.createdAt);
      const diffDays = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) groups['Today'].push(lead);
      else if (diffDays === 1) groups['Yesterday'].push(lead);
      else if (diffDays <= 7) groups['This Week'].push(lead);
      else groups['Older'].push(lead);
    });

    return groups;
  };

  const getStatusBadge = (lead) => {
    if (!lead.viewedBy) return { text: 'New', color: 'bg-red-100 text-red-700' };
    if (lead.status === 'contacted') return { text: 'Contacted', color: 'bg-blue-100 text-blue-700' };
    if (lead.status === 'converted') return { text: 'Paid', color: 'bg-green-100 text-green-700' };
    return { text: 'Viewed', color: 'bg-gray-100 text-gray-700' };
  };

  const getNextAction = (lead) => {
    if (!lead.viewedBy) {
      return { label: 'Mark Viewed', action: () => markAsViewed(lead.id), icon: Eye, color: 'bg-blue-600 hover:bg-blue-700' };
    }
    if (lead.status === 'active' || lead.status === 'viewed') {
      return { label: 'Mark Contacted', action: () => markAsContacted(lead.id), icon: Check, color: 'bg-green-600 hover:bg-green-700' };
    }
    if (lead.status === 'contacted') {
      return { label: 'Convert', action: () => convertLead(lead.id), icon: DollarSign, color: 'bg-purple-600 hover:bg-purple-700' };
    }
    return null;
  };

  const unviewedCount = leads.filter(l => !l.viewedBy && l.status === 'active').length;
  const groupedLeads = groupLeadsByTime(leads);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            Leads
            {unviewedCount > 0 && (
              <span className="inline-flex items-center justify-center w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full">
                {unviewedCount}
              </span>
            )}
          </h2>
          <p className="text-sm text-gray-600 mt-1">Customers who started booking but didn&apos;t complete checkout</p>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('active')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              filter === 'active' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Active ({leads.filter(l => l.status === 'active' || !l.viewedBy).length})
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              filter === 'all' ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
        </div>
      </div>

      {leads.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No leads yet</p>
          <p className="text-sm text-gray-500 mt-1">
            Leads are captured when customers click &quot;Review Booking&quot;
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedLeads).map(([timeGroup, groupLeads]) => {
            if (groupLeads.length === 0) return null;
            
            return (
              <div key={timeGroup}>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">{timeGroup}</h3>
                
                {/* Mobile: Card View */}
                <div className="sm:hidden space-y-3">
                  {groupLeads.map(lead => {
                    const status = getStatusBadge(lead);
                    const nextAction = getNextAction(lead);
                    
                    return (
                      <div key={lead.id} className={`bg-white rounded-lg border p-4 ${!lead.viewedBy ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="font-semibold text-gray-900">{lead.customerName}</div>
                            <div className="text-xs text-gray-600 mt-1">{lead.customerEmail}</div>
                            <div className="text-xs text-gray-600">{lead.customerPhone}</div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${status.color}`}>
                            {status.text}
                          </span>
                        </div>
                        <div className="text-sm text-gray-900 mb-1">{lead.serviceName}</div>
                        <div className="text-xs text-gray-500 mb-2">${lead.totalAmount?.toFixed(2)}</div>
                        <div className="text-xs text-gray-600 mb-3">{formatDateTime(lead.preferredDate, lead.preferredTime)}</div>
                        <div className="flex gap-2">
                          {nextAction && (
                            <button
                              onClick={nextAction.action}
                              className={`flex-1 text-xs px-3 py-1.5 text-white rounded transition ${nextAction.color}`}
                            >
                              {nextAction.label}
                            </button>
                          )}
                          <button
                            onClick={() => deleteLead(lead.id)}
                            className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 transition"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop: Table View */}
                <div className="hidden sm:block bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Phone</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Service</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Intended Booking</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {groupLeads.map(lead => {
                        const status = getStatusBadge(lead);
                        const nextAction = getNextAction(lead);
                        
                        return (
                          <tr key={lead.id} className={`hover:bg-gray-50 transition ${!lead.viewedBy ? 'bg-blue-50' : ''}`}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{lead.customerName}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{lead.customerEmail}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{lead.customerPhone}</td>
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-900">{lead.serviceName}</div>
                              <div className="text-xs text-gray-500">${lead.totalAmount?.toFixed(2)}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {formatDateTime(lead.preferredDate, lead.preferredTime)}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${status.color}`}>
                                {status.text}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-2">
                                {nextAction && (
                                  <button
                                    onClick={nextAction.action}
                                    className={`p-1.5 text-white rounded transition ${nextAction.color}`}
                                    title={nextAction.label}
                                  >
                                    <nextAction.icon className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={() => deleteLead(lead.id)}
                                  className="p-1.5 hover:bg-red-100 rounded transition"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LeadsTab;
